import React, { useState } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Avatar,
  Fade,
  Zoom,
  Paper,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import LaunchIcon from "@mui/icons-material/Launch";
import AppsIcon from "@mui/icons-material/Apps";

const AppSwitcherSidebar = ({ isDarkTheme }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hoverTimeoutRef = React.useRef(null);
  const leaveTimeoutRef = React.useRef(null);

  const handleMouseEnter = () => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    
    // Slight delay before expanding
    hoverTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, 50);
  };

  const handleMouseLeave = () => {
    // Clear any pending hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Immediate collapse for smooth feel
    leaveTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, 150);
  };

  React.useEffect(() => {
    // Cleanup timeouts on unmount
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  // Define your applications here
  const applications = [
    {
      name: "Orunal Staff",
      description: "Staff Management System",
      icon: <DashboardIcon />,
      url: "/task-dashboard",
      color: "#1976d2",
      bgColor: "rgba(25, 118, 210, 0.15)",
      isExternal: false,
    },
    {
      name: "Oruclicks",
      description: "Marketing Platform",
      icon: <TrendingUpIcon />,
      url: "https://www.oruclicks.com",
      color: "#ff6b35",
      bgColor: "rgba(255, 107, 53, 0.15)",
      isExternal: true,
    },
    {
      name: "Orucore",
      description: "CRM Tool",
      icon: <BusinessCenterIcon />,
      url: "https://www.orucore.com",
      color: "#2e7d32",
      bgColor: "rgba(46, 125, 50, 0.15)",
      isExternal: true,
    },
    {
      name: "Oruland",
      description: "Real Estate Platform",
      icon: <HomeWorkIcon />,
      url: "https://www.oruland.com",
      color: "#0288d1",
      bgColor: "rgba(2, 136, 209, 0.15)",
      isExternal: true,
    },
    // {
    //   name: "HR Portal",
    //   description: "Human Resources",
    //   icon: <PeopleIcon />,
    //   url: "/people",
    //   color: "#ed6c02",
    //   bgColor: "rgba(237, 108, 2, 0.15)",
    //   isExternal: false,
    // },
    // {
    //   name: "My Tasks",
    //   description: "Task Management",
    //   icon: <AssignmentIcon />,
    //   url: "/my-task",
    //   color: "#9c27b0",
    //   bgColor: "rgba(156, 39, 176, 0.15)",
    //   isExternal: false,
    // },
    // {
    //   name: "Analytics",
    //   description: "Business Reports",
    //   icon: <BarChartIcon />,
    //   url: "/dashboards",
    //   color: "#7b1fa2",
    //   bgColor: "rgba(123, 31, 162, 0.15)",
    //   isExternal: false,
    // },
    // {
    //   name: "Settings",
    //   description: "System Configuration",
    //   icon: <SettingsIcon />,
    //   url: "/settings",
    //   color: "#616161",
    //   bgColor: "rgba(97, 97, 97, 0.15)",
    //   isExternal: false,
    // },
  ];

  const handleAppClick = (app) => {
    if (app.isExternal) {
      window.open(app.url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = app.url;
    }
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: "fixed",
        right: 0,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 1200,
        transition: "width 0.3s ease-in-out",
        width: isExpanded ? { xs: "220px", sm: "240px", md: "280px" } : { xs: "35px", sm: "45px", md: "60px" },
        willChange: "width",
        pointerEvents: "auto",
        display: { xs: "none", sm: "block" }, // Hide on phones, show on tablets and up
      }}
    >
      <Paper
        elevation={12}
        sx={{
          background: isDarkTheme
            ? "linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px 0 0 20px",
          overflow: "hidden",
          border: `1px solid ${
            isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
          }`,
          boxShadow: isDarkTheme
            ? "0 8px 32px rgba(0, 0, 0, 0.5)"
            : "0 8px 32px rgba(0, 0, 0, 0.15)",
          transition: "box-shadow 0.3s ease",
          maxHeight: { xs: "65vh", sm: "75vh", md: "90vh" },
          display: "flex",
          flexDirection: "column",
          marginLeft: { xs: "-4px", sm: "-6px", md: "-8px" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: isDarkTheme
              ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
              : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "#fff",
            p: { xs: 0.5, sm: 0.75, md: 1.5 },
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1, md: 1.5 },
            minHeight: { xs: "38px", sm: "45px", md: "60px" },
            flexShrink: 0,
            borderRadius: "20px 0 0 0",
          }}
        >
          <Avatar
            sx={{
              width: { xs: 24, sm: 28, md: 38 },
              height: { xs: 24, sm: 28, md: 38 },
              background: "rgba(255, 255, 255, 0.25)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              flexShrink: 0,
            }}
          >
            <AppsIcon sx={{ fontSize: { xs: 14, sm: 16, md: 22 } }} />
          </Avatar>
          
          {isExpanded && (
          <Box 
            sx={{ 
              flex: 1,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "11px", sm: "13px", md: "15px" },
                lineHeight: 1.3,
                letterSpacing: "0.3px",
              }}
            >
              Apps
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "8.5px", sm: "9.5px", md: "11px" },
                opacity: 0.95,
                lineHeight: 1.2,
                fontWeight: 500,
              }}
            >
              Quick Access Hub
            </Typography>
          </Box>
          )}
        </Box>

        {/* App List */}
        <Box
          sx={{
            py: { xs: 0.25, sm: 0.5, md: 1 },
            px: { xs: 0.25, sm: 0.4, md: 0.5 },
            flex: 1,
            overflowY: "auto",
            pointerEvents: isExpanded ? "auto" : "none",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: isDarkTheme
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDarkTheme
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.2)",
              borderRadius: "10px",
            },
          }}
        >
          {applications.map((app, index) => (
            <Box
              key={index}
              onClick={() => handleAppClick(app)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.6, sm: 0.75, md: 1.5 },
                p: { xs: 0.4, sm: 0.5, md: 1.2 },
                mx: { xs: 0.2, sm: 0.25, md: 0.5 },
                my: { xs: 0.2, sm: 0.25, md: 0.5 },
                cursor: "pointer",
                borderRadius: "14px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                background: "transparent",
                border: `1.5px solid transparent`,
                willChange: "transform",
                "&:hover": {
                  background: isDarkTheme
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)",
                  borderColor: app.color,
                  transform: "translateX(-5px) scale(1.03)",
                  boxShadow: `0 6px 24px ${app.color}45`,
                  "& .app-icon": {
                    transform: "scale(1.2) rotate(8deg)",
                    background: app.bgColor,
                    boxShadow: `0 4px 12px ${app.color}40`,
                  },
                  "& .launch-icon": {
                    opacity: 1,
                    transform: "translateX(0) rotate(-45deg)",
                  },
                },
              }}
            >
                {/* App Icon */}
                <Avatar
                  className="app-icon"
                  sx={{
                    width: { xs: 26, sm: 30, md: 42 },
                    height: { xs: 26, sm: 30, md: 42 },
                    background: app.bgColor,
                    color: app.color,
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                    fontSize: { xs: "13px", sm: "15px", md: "22px" },
                    boxShadow: `0 2px 8px ${app.color}25`,
                  }}
                >
                  {app.icon}
                </Avatar>

                {/* App Info (visible when expanded) */}
                <Box
                  sx={{
                    display: isExpanded ? "flex" : "none",
                    opacity: isExpanded ? 1 : 0,
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    gap: 0.3,
                    transition: "opacity 0.2s ease-in-out",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "10px", sm: "11px", md: "13.5px" },
                      color: isDarkTheme ? "#fff" : "#000",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      letterSpacing: "0.2px",
                    }}
                  >
                    {app.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "8px", sm: "8.5px", md: "10.5px" },
                      color: isDarkTheme
                        ? "rgba(255,255,255,0.65)"
                        : "rgba(0,0,0,0.65)",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 500,
                    }}
                  >
                    {app.description}
                  </Typography>
                </Box>

                {/* External Link Icon */}
                {app.isExternal && isExpanded && (
                  <LaunchIcon
                    className="launch-icon"
                    sx={{
                      fontSize: 15,
                      color: isDarkTheme
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.5)",
                      opacity: 0,
                      transform: "translateX(-5px)",
                      transition: "all 0.3s ease",
                      flexShrink: 0,
                    }}
                  />
                )}

                {/* Tooltip for collapsed state */}
                {!isExpanded && (
                  <Tooltip
                    title={
                      <Box sx={{ textAlign: "center", py: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "12px", mb: 0.3 }}
                        >
                          {app.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "10px" }}>
                          {app.description}
                        </Typography>
                      </Box>
                    }
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                      }}
                    />
                  </Tooltip>
                )}
            </Box>
          ))}
        </Box>

        {/* Footer - Expand Indicator */}
        <Box
          sx={{
            p: 1.2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: isDarkTheme
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.03)",
            borderTop: `1px solid ${
              isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
            }`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              color: isDarkTheme
                ? "rgba(255,255,255,0.55)"
                : "rgba(0,0,0,0.55)",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontWeight: 500,
              opacity: isExpanded ? 1 : 0,
              transition: "opacity 0.2s ease-in-out",
              whiteSpace: "nowrap",
            }}
          >
            {isExpanded ? "← Hover to explore apps" : ""}
          </Typography>
        </Box>
      </Paper>

      {/* Pulse indicator when collapsed */}
      {!isExpanded && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "18px",
            transform: "translateY(-50%)",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1976d2, #42a5f5)",
            boxShadow: "0 0 8px rgba(25, 118, 210, 0.6)",
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%, 100%": {
                opacity: 1,
                transform: "translateY(-50%) scale(1)",
                boxShadow: "0 0 8px rgba(25, 118, 210, 0.6)",
              },
              "50%": {
                opacity: 0.6,
                transform: "translateY(-50%) scale(1.4)",
                boxShadow: "0 0 16px rgba(25, 118, 210, 0.4)",
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default AppSwitcherSidebar;
